import { Component, Element, Event, EventEmitter, h, Host, Listen, Prop, Watch } from '@stencil/core';
import clsx from 'clsx';
import { v4 as uuidv4 } from 'uuid';

import store from '@/store';

@Component({
  tag: 'fds-radio',
  styleUrl: 'styles/radio.scss',
  scoped: true,
})
export class Radio {
  private ariaLabelId = uuidv4();

  @Element() el!: HTMLFdsRadioElement;

  /**
   * Sets the name attribute for the component, if not set autogenerated uuid will be used.
   */
  @Prop() name: string = uuidv4();

  /**
   * Sets the label for entire radio group
   */
  @Prop() label!: string;

  /**
   * If true, will hide the label. It will exist in the DOM, just not be visible
   */
  @Prop() hideLabel = false;

  /**
   * Sets the name attribute for the component, if not set autogenerated uuid will be used.
   */
  @Prop() defaultValue: string;

  /**
   * If true, will render the radios vertically
   */
  @Prop() vertical = false;

  /**
   * If true, the component will be disabled, and users cannot interact with it
   */
  @Prop({ reflect: true }) disabled = false;

  @Event() fdsChange: EventEmitter<string>;

  @Watch('disabled')
  disabledChanged(isDisabled: boolean) {
    const radios = this.getRadios();

    for (const radio of radios) {
      radio.disabled = isDisabled;

      if (this.disabled || radio.disabled) {
        radio.setTabIndex(-1);
        continue;
      }

      radio.setTabIndex(0);
    }
  }

  @Watch('defaultValue')
  defaultStepChanged() {
    this.initChecked(this.getRadios());
  }

  componentWillLoad() {
    this.initRadios();
  }

  @Listen('fdsRadioChecked')
  handleChecked(event: CustomEvent<string>) {
    const radios = this.getRadios();
    for (const radio of radios) {
      const shouldBeChecked = radio.getAttribute('value') === event.detail;
      radio.setTabIndex(shouldBeChecked ? 0 : -1);
      radio.isChecked = shouldBeChecked;
    }

    this.fdsChange.emit(event.detail);
    event.stopPropagation();
  }

  @Listen('keydown')
  handleKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowUp':
      case 'ArrowLeft': {
        event.preventDefault();
        this.selectNewRadio('previous');
        break;
      }
      case 'ArrowDown':
      case 'ArrowRight': {
        event.preventDefault();
        this.selectNewRadio('next');
        break;
      }
    }
  }

  private initRadios() {
    const radios = this.getRadios();

    for (const radio of radios) {
      radio.name = this.name;

      if (this.disabled) {
        radio.disabled = true;
      }
    }

    this.initChecked(radios);
  }

  private initChecked(radios: HTMLFdsRadioItemElement[]) {
    const defaultChecked = radios.find((r) => r.getAttribute('value') === this.defaultValue);

    if (!defaultChecked) {
      for (const radio of radios) {
        radio.setTabIndex(radio.disabled ? -1 : 0);
        radio.isChecked = false;
      }
      return;
    }

    const otherRadios = radios.filter((r) => r !== defaultChecked);
    for (const radio of otherRadios) {
      radio.isChecked = false;
      radio.setTabIndex(-1);
    }
    defaultChecked.isChecked = true;
    defaultChecked.setTabIndex(defaultChecked.disabled ? -1 : 0);
  }

  private getRadios(): Array<HTMLFdsRadioItemElement> {
    // eslint-disable-next-line unicorn/prefer-spread
    return Array.from(this.el.querySelectorAll('fds-radio-item'));
  }

  private getNewSelectedIndex(order: 'next' | 'previous', radios: HTMLFdsRadioItemElement[]) {
    const enabledRadios = radios.filter((r) => !r.disabled);
    const selected = radios.findIndex((r) => r.isChecked);

    // if there is no other radio to select, return current selected index
    if (enabledRadios.length <= 1) {
      return selected;
    }

    let indexToReturn: number;
    let indexToCheck = selected;
    const increment = order === 'next' ? 1 : -1;

    do {
      indexToCheck += increment;

      if (indexToCheck > radios.length - 1) {
        indexToCheck = 0;
      }

      if (indexToCheck < 0) {
        indexToCheck = radios.length - 1;
      }

      if (!radios[indexToCheck].disabled) {
        indexToReturn = indexToCheck;
      }
    } while (indexToReturn === undefined);

    return indexToReturn;
  }

  private updateRadios(radios: HTMLFdsRadioItemElement[], newSelectedIndex: number) {
    for (const [index, value] of radios.entries()) {
      if (value.disabled) {
        continue;
      }

      value.setTabIndex(index === newSelectedIndex ? 0 : -1);
      value.isChecked = index === newSelectedIndex ? true : false;
      if (index === newSelectedIndex) {
        value.setFocus();
        value.emitChecked();
      }
    }
  }

  private selectNewRadio(order: 'next' | 'previous') {
    const radios = this.getRadios();
    const newSelectedIndex = this.getNewSelectedIndex(order, radios);

    this.updateRadios(radios, newSelectedIndex);
  }

  render() {
    return (
      <Host
        class={clsx(store.mode, 'fds-radio', {
          'fds-radio--vertical': this.vertical,
          'fds-radio--disabled': this.disabled,
        })}
        role="radiogroup"
        aria-orientation={this.vertical ? 'vertical' : 'horizontal'}
        aria-label={this.hideLabel ? this.label : undefined}
        aria-labelledby={this.hideLabel ? undefined : this.ariaLabelId}
      >
        <div class={clsx('fds-radio__header', { 'fds-radio__header--hidden': this.hideLabel })}>
          <label id={this.ariaLabelId}>{this.label}</label>
        </div>
        <div class="fds-radio__items">
          <slot></slot>
        </div>
      </Host>
    );
  }
}
